import { unwrap } from "@sagot/api-client";
import { HOME_COUNTRY, NAME_MAX_LENGTH, WORKSPACE_LANGUAGES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Input, Select, toast } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../../lib/api-errors";
import { queryKeys } from "../../../../lib/query-keys";
import { TimezoneOptions } from "../../../../lib/region-pickers";
import { zodResolver } from "../../../../lib/zod-resolver";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceDetails, useWorkspaceId } from "../../../../session/workspace-context";
import { wsKeys, type OrganizationOut } from "../../shared";
import { useOrganization } from "../use-settings";
import { BillingRegionCard } from "./billing-region-card";
import { IndustryProfileCard } from "./industry-profile-card";
import { LogoField } from "./logo-field";
import { CardActions, CardSkeleton, PanelError, useCanManage } from "./panel-kit";

const HTTPS_URL = /^https:\/\/[^\s/$.?#].[^\s]*$/i;

function useOrgSchema() {
  const { t } = useTranslation();
  return z.object({
    name: z.string().trim().min(1, t("settings.org.nameRequired")).max(NAME_MAX_LENGTH),
    website: z.string().trim().refine((v) => v === "" || HTTPS_URL.test(v), t("settings.org.websiteInvalid")),
    timezone: z.string().min(1),
    default_language: z.string().min(1),
    address: z.string().trim().max(500),
    city: z.string().trim().max(200),
  });
}
type OrgValues = z.infer<ReturnType<typeof useOrgSchema>>;

function toValues(org: OrganizationOut): OrgValues {
  return {
    name: org.name,
    website: org.website ?? "",
    timezone: org.timezone,
    default_language: org.default_language,
    address: org.address ?? "",
    city: org.city ?? "",
  };
}

const nullable = (value: string) => (value.trim() === "" ? null : value.trim());

function OrganizationForm({ org }: { org: OrganizationOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const manage = useCanManage("settings.manage");
  const { register, control, handleSubmit, reset, formState } = useForm<OrgValues>({ resolver: zodResolver(useOrgSchema()), defaultValues: toValues(org) });
  useEffect(() => {
    reset(toValues(org));
  }, [org, reset]);
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: wsKeys.organization(wsId) }), queryClient.invalidateQueries({ queryKey: queryKeys.workspace(wsId) })]);
  const country = useWorkspaceDetails().data?.country ?? HOME_COUNTRY;

  const onSubmit = handleSubmit(async (v) => {
    try {
      const body = { name: v.name, website: nullable(v.website), timezone: v.timezone, default_language: v.default_language, address: nullable(v.address), city: nullable(v.city) };
      await unwrap(api.PATCH("/v1/settings/organization", { body }));
      toast.success(t("settings.org.saved"));
      await Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: queryKeys.me })]);
    } catch (caught) {
      toast.error(message(caught));
    }
  });
  const locked = !manage.allowed;
  return (
    <form className="card" onSubmit={(event) => void onSubmit(event)} noValidate aria-labelledby="org-title">
      <div className="card-header">
        <div>
          <h2 id="org-title" className="card-title">
            {t("settings.org.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("settings.org.subtitle")}</p>
        </div>
      </div>
      <fieldset className="card-body space-y-5" disabled={locked}>
        <LogoField org={org} disabled={locked} onSaved={refresh} />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label={t("settings.org.name")} error={formState.errors.name?.message}>
            <Input autoComplete="organization" {...register("name")} />
          </Field>
          <Field label={t("settings.org.website")} error={formState.errors.website?.message}>
            <Input type="url" placeholder="https://" autoComplete="url" {...register("website")} />
          </Field>
          <Field label={t("settings.org.timezone")}>
            <Controller
              control={control}
              name="timezone"
              render={({ field }) => (
                <Select {...field}>
                  <TimezoneOptions country={country} current={org.timezone} format={(tz) => t(`settings.org.timezones.${tz.replace("/", "_")}`, { defaultValue: tz })} />
                </Select>
              )}
            />
          </Field>
          <Field label={t("settings.org.language")} hint={t("settings.org.languageHint")}>
            <Select {...register("default_language")}>
              {WORKSPACE_LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>
                  {t(`settings.org.languages.${lang}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("settings.org.workspaceUrl")} hint={t("settings.org.workspaceUrlHint")}>
            <Input readOnly value={org.workspace_url} className="bg-surface-2 font-mono text-xs text-fg-muted" />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label={t("settings.org.address")} error={formState.errors.address?.message} className="md:col-span-2">
            <Input autoComplete="street-address" {...register("address")} />
          </Field>
          <Field label={t("settings.org.city")} error={formState.errors.city?.message}>
            <Input autoComplete="address-level2" {...register("city")} />
          </Field>
        </div>
      </fieldset>
      <CardActions>
        <Button variant="secondary" disabled={locked || !formState.isDirty} onClick={() => { reset(toValues(org)); }}>
          {t("settings.discard")}
        </Button>
        <Button type="submit" disabled={locked} loading={formState.isSubmitting} title={manage.reason}>
          {t("settings.saveChanges")}
        </Button>
      </CardActions>
    </form>
  );
}

/** Organization panel: name, website, timezone, language, address, logo; industry, goals and
 * profile answers (+ "Re-suggest setup"); billing region. */
export function OrganizationPanel() {
  const org = useOrganization();
  const workspace = useWorkspaceDetails();
  const manageWorkspace = useCanManage("workspace.manage");
  if (org.isPending) return <CardSkeleton rows={6} />;
  if (org.isError) return <PanelError error={org.error} onRetry={() => void org.refetch()} />;
  return (
    <div className="space-y-6">
      <OrganizationForm org={org.data} />
      {workspace.data ? <IndustryProfileCard workspace={workspace.data} canManage={manageWorkspace.allowed} /> : null}
      <BillingRegionCard />
    </div>
  );
}
